// The approval cycle for one privileged Control Plane act, as one component.
//
// Publish, rollback and bootstrap are three different acts with the same
// governance shape, and before this existed the console implemented none of
// it: `publish()`, `rollback()` and `bootstrap()` were called with no
// proposalId at all, which against the current backend means every one of them
// returns 403 `proposal_required`. The operator saw "HTTP 403 unauthorized".
//
// ─── The cycle ────────────────────────────────────────────────────────────
//
//   REVIEW      what the act is, against what, on which lane, at what digest,
//               under which risk class and approval requirement
//   REQUEST     ask the SERVER to mint a proposal — it decides lane, class,
//               threshold and target; this console decides none of them
//   OBSERVE     read the proposal's state back from the server, by id
//   EXECUTE     send the exact proposalId with the act
//
// Every step is separated because they are separately refusable, and an
// operator who cannot tell "not approved yet" from "approved for something
// else" cannot act on either.
//
// ─── Three rules this component holds ─────────────────────────────────────
//
// 1. THE REVIEW IS ALWAYS AVAILABLE. Under the write freeze the request and
//    execute controls are disabled, and the review above them is not. A frozen
//    console that also refuses to show what WOULD happen is a console an
//    operator cannot prepare with.
//
// 2. APPROVAL STATE COMES FROM THE SERVER. `describeProposal` reads the
//    server's `status` and `thresholdMet`; nothing here turns elapsed time,
//    an approval count, or a click on another screen into APPROVED.
//
// 3. THE PROPOSAL MUST BE FOR THIS ACT. `checkBinding` compares the
//    proposal's own payload against the act on screen before the execute
//    control is offered. The server re-derives and re-checks all of it — this
//    is what stops the console OFFERING a pair it can already see is wrong.

import { useCallback, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ConfirmDialog, type TypedConfirmation } from "../components/ConfirmDialog";
import {
  AuthorityNotice,
  GAOSAFE_APPROVAL_LABEL,
  GAOSAFE_APPROVAL_REASON,
  useControlPlaneWritesBlocked,
} from "../authority";
import { getProposal, describeProposal, type ProposalDetailResponse } from "../api/approvals";
import type { ProposalResponse } from "./api";
import { classifyControlPlaneError, RECOVERY_TEXT, type ControlPlaneFailure } from "./errors";
import {
  approvalRequirementText,
  expectedGovernance,
  reconcile,
  RISK_CLASS_TEXT,
  type GovernanceDrift,
} from "./governance";
import { checkBinding, type ControlPlaneAct } from "./proposal";
import { laneLabel } from "./theme";
import { Btn, Card, Empty, Hash, Kv, KvList, StatusChip } from "./ui";
import type { StatusTone } from "./styles";

/** A row in the action review. `value` is a node so a hash can be rendered
 *  with its own truncation and a revision with its own emphasis. */
export interface ReviewRow {
  key: string;
  value: ReactNode;
}

export interface GovernedActionProps {
  /** The five terms a proposal commits to. `lane` is the server's answer. */
  act: ControlPlaneAct;
  /** Human name of the act, e.g. "Publish". */
  title: string;
  /** One sentence: what this does to the world if it succeeds. */
  consequence: ReactNode;
  /** ACTION / PRODUCT / ENVIRONMENT / CURRENT / TARGET / DIGEST … */
  rows: ReadonlyArray<ReviewRow>;
  /** Extra review content — a diff, a visibility impact list, a genesis
   *  summary. Rendered above the governance block. */
  children?: ReactNode;
  /** Set when the act is not currently possible for a NON-authority reason:
   *  an invalid draft, no changes, no second revision. Disables request and
   *  execute, and is stated rather than shown as a dead button. */
  blockedReason?: string | null;
  /** Product-lane type-to-confirm, passed through to the dialog. */
  requireTypedConfirmation?: TypedConfirmation;
  /** Performs the act. Receives the exact proposalId the operator approved. */
  onExecute: (proposalId: string) => Promise<void>;
  /** Mints the proposal. Returns the SERVER's answer verbatim. */
  onRequestApproval: () => Promise<ProposalResponse>;
  /** Label on the execute control, e.g. "Publish to PRODUCT". */
  executeLabel: string;
  tone?: "warning" | "danger";
}

const STATUS_TONE: Readonly<Record<string, StatusTone>> = {
  PENDING: "warn",
  APPROVED: "ok",
  REJECTED: "danger",
  EXPIRED: "danger",
  CONSUMED: "muted",
  CANCELLED: "muted",
};

function FailureNote({ f }: { f: ControlPlaneFailure }) {
  const recovery = RECOVERY_TEXT[f.recovery];
  return (
    <div
      role="alert"
      style={{
        border: "1px solid var(--danger)",
        background: "var(--danger-bg, #2a1416)",
        borderRadius: 4,
        padding: "10px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <b style={{ color: "var(--danger)" }}>{f.title}</b>
      <span>{f.detail}</span>
      {recovery !== "" && (
        <span style={{ color: "var(--fg-dim)" }}>Next: {recovery}.</span>
      )}
      {f.reason !== null && (
        <span style={{ color: "var(--fg-dim)", fontSize: 11 }}>
          server reason: <code>{f.reason}</code>
          {f.status !== null ? ` · HTTP ${f.status}` : ""}
        </span>
      )}
    </div>
  );
}

export function GovernedAction(props: GovernedActionProps) {
  const { act } = props;
  const writesBlocked = useControlPlaneWritesBlocked();

  const [minted, setMinted] = useState<ProposalResponse | null>(null);
  const [proposalId, setProposalId] = useState("");
  const [detail, setDetail] = useState<ProposalDetailResponse | null>(null);
  const [drift, setDrift] = useState<GovernanceDrift>({ drifted: false });
  const [busy, setBusy] = useState<"propose" | "check" | "execute" | null>(null);
  const [failure, setFailure] = useState<ControlPlaneFailure | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const expected = expectedGovernance(act.operation, act.lane);

  const check = useCallback(
    async (id: string) => {
      setBusy("check");
      setFailure(null);
      try {
        setDetail(await getProposal(id));
      } catch (e) {
        setDetail(null);
        setFailure(classifyControlPlaneError(e));
      } finally {
        setBusy(null);
      }
    },
    [],
  );

  async function requestApproval() {
    setBusy("propose");
    setFailure(null);
    setDetail(null);
    try {
      const p = await props.onRequestApproval();
      setMinted(p);
      setProposalId(p.proposalId);
      setDrift(reconcile(expected, p));
      // Read the proposal straight back rather than assuming its state. It is
      // `proposed`, but that is the server's word to say, not this console's.
      await check(p.proposalId);
    } catch (e) {
      setFailure(classifyControlPlaneError(e));
      setBusy(null);
    }
  }

  async function execute() {
    const id = proposalId.trim();
    if (id === "") return;
    setBusy("execute");
    setFailure(null);
    try {
      await props.onExecute(id);
      setConfirmOpen(false);
    } catch (e) {
      setFailure(classifyControlPlaneError(e));
    } finally {
      setBusy(null);
    }
  }

  const described = detail === null ? null : describeProposal(detail);
  const bound = detail === null ? null : checkBinding(act, detail);
  const serverExecutable = described?.executable === true;
  const canExecute =
    !writesBlocked &&
    (props.blockedReason ?? null) === null &&
    serverExecutable &&
    bound !== null &&
    bound.ok &&
    busy === null;

  return (
    <Card
      accent={act.lane === "production" ? "danger" : "warn"}
      title={`${props.title} — review and approval`}
      note={`${act.operation} · ${act.productId} · ${laneLabel(act.lane)}`}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>{props.consequence}</div>

        {props.children}

        {/* ── §11 ACTION REVIEW ───────────────────────────────────────── */}
        <section>
          <KvList>
            {props.rows.map((r) => (
              <Kv key={r.key} k={r.key}>
                {r.value}
              </Kv>
            ))}
            <Kv k="risk class">
              {expected === null ? (
                <span style={{ color: "var(--fg-dim)" }}>
                  UNKNOWN — this console has not been told which lane it is on,
                  and the class depends on the lane.
                </span>
              ) : minted !== null ? (
                <>
                  <b>{minted.actionType}</b>{" "}
                  <span style={{ color: "var(--fg-dim)" }}>
                    — as minted by the server
                  </span>
                </>
              ) : (
                <>
                  {RISK_CLASS_TEXT[expected.riskClass]}{" "}
                  <span style={{ color: "var(--fg-dim)" }}>
                    (expected — the server decides, and states it on the proposal)
                  </span>
                </>
              )}
            </Kv>
            <Kv k="approval">
              {expected === null ? (
                <span style={{ color: "var(--fg-dim)" }}>UNKNOWN</span>
              ) : minted !== null ? (
                <>
                  <b>{minted.thresholdRequired}</b> approval
                  {minted.thresholdRequired === 1 ? "" : "s"} required, as recorded
                  on the proposal
                </>
              ) : (
                <>
                  {approvalRequirementText(expected)}{" "}
                  <span style={{ color: "var(--fg-dim)" }}>(expected)</span>
                </>
              )}
            </Kv>
            <Kv k="action name">
              <code style={{ color: "var(--fg-dim)" }}>
                {expected?.actionName ?? "unknown"}
              </code>
            </Kv>
          </KvList>
        </section>

        {drift.drifted && (
          <div
            role="alert"
            style={{
              border: "1px solid var(--warn)",
              borderRadius: 4,
              padding: "10px 12px",
            }}
          >
            <b style={{ color: "var(--warn)" }}>
              The server required something other than this console expected
            </b>
            <div>{drift.detail}</div>
          </div>
        )}

        {props.blockedReason != null && (
          <div style={{ color: "var(--warn)" }}>{props.blockedReason}</div>
        )}

        {writesBlocked && (
          <AuthorityNotice
            scope={`Requesting an approval and executing a ${act.operation} are both blocked — the server refuses every Control Plane mutation, including minting a proposal, while the write gate is closed.`}
          />
        )}

        {/* ── §12 PROPOSAL CREATION ───────────────────────────────────── */}
        <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <Btn
              tone="primary"
              onClick={() => void requestApproval()}
              disabled={
                writesBlocked || busy !== null || (props.blockedReason ?? null) !== null
              }
              title={writesBlocked ? GAOSAFE_APPROVAL_REASON : undefined}
            >
              {busy === "propose" ? "Requesting…" : "Request approval"}
            </Btn>
            <span style={{ color: "var(--fg-dim)" }}>
              The server mints the proposal: it chooses the lane, the action name,
              the class, the threshold and the exact target. This console sends
              only which operation, and for a rollback which revision.
            </span>
          </div>

          <label style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ color: "var(--fg-dim)" }}>
              or use an existing approved proposal
            </span>
            <input
              type="text"
              aria-label="proposalId"
              value={proposalId}
              placeholder="proposal id"
              onChange={(e) => {
                setProposalId(e.target.value);
                setDetail(null);
                setMinted(null);
                setDrift({ drifted: false });
              }}
              style={{ minWidth: 280, padding: "4px 6px", fontSize: 12 }}
            />
            <Btn
              onClick={() => void check(proposalId.trim())}
              disabled={proposalId.trim() === "" || busy !== null}
            >
              {busy === "check" ? "Checking…" : "Check approval state"}
            </Btn>
          </label>
        </section>

        {failure !== null && <FailureNote f={failure} />}

        {/* ── §13/§14 APPROVAL STATE ──────────────────────────────────── */}
        {detail !== null && described !== null && (
          <section
            style={{
              border: "1px solid var(--border)",
              borderRadius: 4,
              padding: "10px 12px",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <StatusChip tone={STATUS_TONE[described.label] ?? "muted"}>
                {described.label}
              </StatusChip>
              <code style={{ color: "var(--fg-dim)" }}>{detail.proposal.id}</code>
              <Link to={`/approvals/${detail.proposal.id}`}>Open in Approvals</Link>
              <Btn
                onClick={() => void check(detail.proposal.id)}
                disabled={busy !== null}
              >
                Re-check
              </Btn>
            </div>
            <div>{described.detail}</div>
            <KvList>
              <Kv k="approvals">
                {detail.proposal.approveCount} of {detail.proposal.thresholdRequired}
                {detail.proposal.rejectCount > 0
                  ? ` · ${detail.proposal.rejectCount} rejection${detail.proposal.rejectCount === 1 ? "" : "s"}`
                  : ""}
              </Kv>
              <Kv k="class">{detail.proposal.actionType}</Kv>
              <Kv k="expires">{detail.proposal.expiresAt}</Kv>
              <Kv k="payload">
                <Hash value={detail.proposal.payloadHash} />
              </Kv>
            </KvList>

            {/* ── §15 EXECUTION HANDOFF: is this proposal for THIS act? ── */}
            {bound !== null &&
              (bound.ok ? (
                <div style={{ color: "var(--ok)" }}>
                  This proposal is bound to this exact action —{" "}
                  {bound.binding.operation} · {bound.binding.productId} ·{" "}
                  {bound.binding.lane} ·{" "}
                  {bound.binding.target === null ? (
                    "no prior target"
                  ) : (
                    <Hash value={bound.binding.target} />
                  )}
                  . The server re-derives and re-checks all five before it acts.
                </div>
              ) : (
                <div role="alert" style={{ color: "var(--danger)" }}>
                  <b>This approval does not authorise the action on screen.</b>{" "}
                  {bound.detail} Executing it would be refused by the server with{" "}
                  <code>{bound.mismatch}</code>, and the attempt would be recorded
                  as a rejected privileged act.
                </div>
              ))}
          </section>
        )}

        {/* ── EXECUTE ─────────────────────────────────────────────────── */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Btn
            tone={props.tone === "danger" ? "danger" : "primary"}
            onClick={() => setConfirmOpen(true)}
            disabled={!canExecute}
            title={
              writesBlocked
                ? GAOSAFE_APPROVAL_REASON
                : props.blockedReason ??
                  (detail === null
                    ? "Request an approval, or check an existing one, first."
                    : !serverExecutable
                      ? "The server does not report this proposal as approved and spendable."
                      : bound !== null && !bound.ok
                        ? bound.detail
                        : undefined)
            }
          >
            {busy === "execute" ? "Working…" : props.executeLabel}
          </Btn>
          {writesBlocked && (
            <StatusChip tone="warn">{GAOSAFE_APPROVAL_LABEL}</StatusChip>
          )}
        </div>

        {detail === null && failure === null && (
          <Empty>
            No approval has been requested for this action yet. Nothing has been
            sent to the server beyond reads.
          </Empty>
        )}
      </div>

      <ConfirmDialog
        writesBlocked={writesBlocked}
        open={confirmOpen}
        title={props.title}
        tone={props.tone ?? "warning"}
        busy={busy === "execute"}
        error={failure === null ? null : `${failure.title} — ${failure.detail}`}
        description={props.consequence}
        rows={[
          ...props.rows,
          { key: "proposal", value: <code>{proposalId.trim()}</code> },
          {
            key: "approvals",
            value:
              detail === null
                ? "—"
                : `${detail.proposal.approveCount} of ${detail.proposal.thresholdRequired}`,
          },
        ]}
        requireTypedConfirmation={props.requireTypedConfirmation}
        confirmLabel={props.executeLabel}
        confirmDisabled={!canExecute}
        onConfirm={() => void execute()}
        onCancel={() => setConfirmOpen(false)}
      />
    </Card>
  );
}
